/**
 * Tutorial State Management Composable
 *
 * Manages the state of the interactive tutorial system including:
 * - Active tutorial tracking
 * - Step navigation
 * - Completion persistence (localStorage)
 * - First-time user flow (guided journey through screens)
 */

import { ref, computed, watch } from 'vue'
import type { Tutorial, TutorialState, TutorialStep, TutorialId } from '@/types/tutorial'
import {
  TUTORIAL_STORAGE_KEY,
  DEFAULT_TUTORIAL_STATE
} from '@/types/tutorial'
import { tutorials } from '@/data/tutorialSteps'

/**
 * Load state from localStorage
 */
function loadState(): TutorialState {
  try {
    const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY)
    if (!stored) return { ...DEFAULT_TUTORIAL_STATE }

    const parsed = JSON.parse(stored)
    return {
      ...DEFAULT_TUTORIAL_STATE,
      ...parsed,
      completedTutorials: parsed.completedTutorials || []
    }
  } catch {
    return { ...DEFAULT_TUTORIAL_STATE }
  }
}

/**
 * Save state to localStorage
 */
function saveState(state: TutorialState): void {
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('[Tutorial] Failed to save state:', e)
  }
}

// Singleton state (shared across components)
const state = ref<TutorialState>(loadState())

/**
 * Tutorial composable for managing interactive tutorial state
 */
export function useTutorial() {
  // Computed properties
  const isActive = computed(() => state.value.activeTutorial !== null)

  const currentTutorial = computed<Tutorial | null>(() => {
    if (!state.value.activeTutorial) return null
    return tutorials[state.value.activeTutorial] || null
  })

  const currentStep = computed<TutorialStep | null>(() => {
    if (!currentTutorial.value) return null
    return currentTutorial.value.steps[state.value.currentStepIndex] || null
  })

  const currentStepIndex = computed(() => state.value.currentStepIndex)

  const totalSteps = computed(() => currentTutorial.value?.steps.length || 0)

  const progress = computed(() => {
    if (totalSteps.value === 0) return 0
    return ((state.value.currentStepIndex + 1) / totalSteps.value) * 100
  })

  const isFirstStep = computed(() => state.value.currentStepIndex === 0)

  const isLastStep = computed(
    () => state.value.currentStepIndex === totalSteps.value - 1
  )

  const isFirstTimeUser = computed(() => {
    return (
      !state.value.hasSeenFirstVisitPrompt && !state.value.disableAutoPrompt
    )
  })

  const isFirstTimeFlow = computed(() => state.value.isFirstTimeFlow)

  /**
   * Check if a tutorial has been completed
   */
  function hasCompletedTutorial(tutorialId: TutorialId): boolean {
    return state.value.completedTutorials.includes(tutorialId)
  }

  /**
   * Start a tutorial
   */
  function startTutorial(tutorialId: TutorialId): void {
    if (!tutorials[tutorialId]) {
      console.warn(`[Tutorial] Unknown tutorial: ${tutorialId}`)
      return
    }

    console.log(`[Tutorial] Starting tutorial: ${tutorialId}`)
    state.value.activeTutorial = tutorialId
    state.value.currentStepIndex = 0
    saveState(state.value)
  }

  /**
   * Start the first-time user guided flow
   * This will guide users through lobby -> create-game -> game
   */
  function startFirstTimeFlow(): void {
    console.log('[Tutorial] Starting first-time user flow')
    state.value.isFirstTimeFlow = true
    state.value.hasSeenFirstVisitPrompt = true
    startTutorial('lobby')
  }

  /**
   * Continue to the next tutorial in the first-time flow
   * Called when user navigates to a new screen
   */
  function continueFirstTimeFlow(screen: TutorialId): void {
    if (!state.value.isFirstTimeFlow) return

    // Complete the current tutorial if one is active (action-required step was performed)
    if (state.value.activeTutorial && state.value.activeTutorial !== screen) {
      const previousTutorial = state.value.activeTutorial
      console.log(`[Tutorial] Completing previous tutorial: ${previousTutorial}`)

      // Mark as completed without clearing active state (we'll set new one)
      if (!state.value.completedTutorials.includes(previousTutorial)) {
        state.value.completedTutorials.push(previousTutorial)
      }
    }

    // Only auto-start if we haven't completed this tutorial yet
    if (!hasCompletedTutorial(screen)) {
      console.log(`[Tutorial] Continuing first-time flow on screen: ${screen}`)
      // Small delay to let the DOM render
      setTimeout(() => {
        startTutorial(screen)
      }, 500)
    }
  }

  /**
   * End the first-time flow (user completed or skipped)
   */
  function endFirstTimeFlow(): void {
    console.log('[Tutorial] Ending first-time user flow')
    state.value.isFirstTimeFlow = false
    saveState(state.value)
  }

  /**
   * Go to next step or complete tutorial if at end
   */
  function nextStep(): void {
    if (!isActive.value) return

    if (state.value.currentStepIndex < totalSteps.value - 1) {
      state.value.currentStepIndex++
      saveState(state.value)
    } else {
      completeTutorial()
    }
  }

  /**
   * Go to previous step
   */
  function previousStep(): void {
    if (!isActive.value || state.value.currentStepIndex === 0) return

    state.value.currentStepIndex--
    saveState(state.value)
  }

  /**
   * Jump to specific step
   */
  function goToStep(index: number): void {
    if (!isActive.value || index < 0 || index >= totalSteps.value) return

    state.value.currentStepIndex = index
    saveState(state.value)
  }

  /**
   * Skip tutorial without marking as completed
   */
  function skipTutorial(): void {
    if (!isActive.value) return

    // If in first-time flow and skipping, end the flow
    if (state.value.isFirstTimeFlow) {
      endFirstTimeFlow()
    }

    state.value.activeTutorial = null
    state.value.currentStepIndex = 0
    saveState(state.value)
  }

  /**
   * Complete tutorial and mark as done
   */
  function completeTutorial(): void {
    if (!state.value.activeTutorial) return

    const completedId = state.value.activeTutorial

    if (!state.value.completedTutorials.includes(completedId)) {
      state.value.completedTutorials.push(completedId)
    }

    state.value.activeTutorial = null
    state.value.currentStepIndex = 0
    saveState(state.value)

    console.log(`[Tutorial] Completed tutorial: ${completedId}`)

    // If this was the game tutorial, end the first-time flow
    if (completedId === 'game' && state.value.isFirstTimeFlow) {
      endFirstTimeFlow()
    }
  }

  /**
   * Mark first-visit prompt as seen
   */
  function markFirstVisitSeen(): void {
    state.value.hasSeenFirstVisitPrompt = true
    saveState(state.value)
  }

  /**
   * Set auto-prompt preference
   */
  function setAutoPromptDisabled(disabled: boolean): void {
    state.value.disableAutoPrompt = disabled
    saveState(state.value)
  }

  /**
   * Reset all tutorial state (for testing/debugging)
   */
  function resetTutorialState(): void {
    state.value = { ...DEFAULT_TUTORIAL_STATE }
    saveState(state.value)
    console.log('[Tutorial] State reset')
  }

  // Watch for changes and persist
  watch(
    state,
    () => {
      saveState(state.value)
    },
    { deep: true }
  )

  return {
    // State
    state,
    isActive,
    currentTutorial,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    isFirstStep,
    isLastStep,
    isFirstTimeUser,
    isFirstTimeFlow,

    // Methods
    startTutorial,
    startFirstTimeFlow,
    continueFirstTimeFlow,
    endFirstTimeFlow,
    nextStep,
    previousStep,
    goToStep,
    skipTutorial,
    completeTutorial,
    hasCompletedTutorial,
    markFirstVisitSeen,
    setAutoPromptDisabled,
    resetTutorialState,

    // Tutorial data
    tutorials
  }
}
